import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import {
  fetchNavPricingDiscountLabel,
  initializeInfluencerReferral,
  influencerReferralStore,
} from '../../vendor/pricing-glass/navDiscount';

import styles from './PricingNavLabel.module.css';

/** Landing experiment buckets that see the pill (same as the landing nav). */
const PILL_BUCKETS = ['a', 'd'];
const COUNTRY_GROUPS = ['platinum', 'gold', 'silver', 'bronze', 'row'] as const;

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  try {
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

/**
 * "Pricing" plus the live discount pill. Resolved in the browser from the
 * worker's `iv_exp_bucket` and `iv_country_group` cookies, because this host's
 * cached HTML is shared by every bucket and country group.
 */
export default function PricingNavLabel({
  label,
  locale,
  below = false,
}: {
  label: string;
  locale?: string;
  /** Desktop: centre the pill under the label, out of flow. */
  below?: boolean;
}) {
  const { asPath } = useRouter();
  const referralState = influencerReferralStore(state => state);
  useEffect(() => {
    void initializeInfluencerReferral();
  }, [asPath]);
  const [discount, setDiscount] = useState<string | null>(null);

  useEffect(() => {
    setDiscount(null);
    const bucket = readCookie('iv_exp_bucket');
    const group = readCookie('iv_country_group');
    const pillExperimentEnabled =
      readCookie('iv_nav_pill_experiment') === 'true';
    if (pillExperimentEnabled && (!bucket || !PILL_BUCKETS.includes(bucket)))
      return;
    if (!COUNTRY_GROUPS.some(g => g === group)) return;
    if (referralState.status === 'idle' || referralState.status === 'resolving')
      return;
    let active = true;
    fetchNavPricingDiscountLabel(
      group as (typeof COUNTRY_GROUPS)[number],
      locale,
      referralState.receipt?.handle,
    )
      .then(next => {
        if (active) setDiscount(next);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [locale, asPath, referralState.status, referralState.receipt?.handle]);

  if (!discount) return <>{label}</>;

  return (
    <span className={below ? 'iv-relative iv-inline-block' : undefined}>
      {label}
      <span
        className={`${styles.pill} ${below ? styles.below : styles.inline}`}
        data-testid="nav-pricing-discount"
      >
        {discount}
      </span>
    </span>
  );
}
